import { create } from 'zustand';
import { QueryDocumentSnapshot, DocumentData } from '@firebase/firestore';
import * as ngeohash from 'ngeohash';
import { Post, PostCategory } from '../types';
import { postsApi, CreatePostData, UpdatePostData } from '../api/posts';
import { uploadImage, thumbnailPathFor, displayPathFor } from '../api/storage';
import { AppError, getErrorMessage } from '../utils/errorHandler';
import { updateById } from '../utils/collections';
import { anonWritesBlocked } from '../constants/auth';
import { withLoading } from './utils/withLoading';
import { getPostVisibilityScope, mergeOwnPosts } from '../utils/postVisibility';
import { deriveStructurePostBadges, type StructureBadge } from '../utils/structureBadges';
import { useAuthStore } from './authStore';
import { createPrefixedLogger } from '../utils/logger';
import { FEED_STALE_MS } from '../constants/posts';

const log = createPrefixedLogger('postsStore');

// Visibility scope for the signed-in (or signed-out) viewer, read at call
// time so queries pick up auth changes without re-wiring subscriptions.
const viewerScope = () => getPostVisibilityScope(useAuthStore.getState().user);

// The News tab shows only text-first announcements — photo observations
// live on the map and in structure threads instead. `posts`/`fetchFeed`/
// `subscribeFeed` below are read only by the feed screen (nothing else
// selects `s.posts`), so the category filter is applied here rather than
// threaded through as a param nobody else would pass.
const FEED_CATEGORY: PostCategory = 'news';

// firestore.rules denies anonymous content writes while ANON_WRITES_ENABLED
// is false; fail here with friendly copy so any call path that skips the
// UI's upgrade prompt doesn't surface a raw permission error. Mirrors the
// anonymous-claim guard in structuresStore.claimStructure.
const assertWritableAccount = () => {
  if (anonWritesBlocked(useAuthStore.getState().user)) {
    throw new AppError('Save your account to post.', 'PERMISSION_DENIED');
  }
};

interface PostsState {
  // Data
  // The News tab's list — scoped server-side to category 'news' (see
  // FEED_CATEGORY). Photo observations (progress/risk) never land here;
  // they surface on the map and in structure threads instead.
  posts: Post[];
  userPosts: Post[];
  structurePosts: Post[];
  // The viewer's own posts on the loaded structure, any visibility. Merged
  // into structurePosts so authors see their private/insurance posts in the
  // thread list (the base query is visibility-filtered by the rules).
  ownStructurePosts: Post[];
  // Live subscription feeding the map's observation marker layer. Holds the
  // most recent N posts globally; consumers filter for location + visibility.
  mapPosts: Post[];
  // Derived from mapPosts (structureId -> badge) — see
  // src/utils/structureBadges.ts. Recomputed everywhere mapPosts changes so
  // the structure marker badge stays in sync without a second subscription.
  structurePostBadges: Record<string, StructureBadge>;
  // Buffer for posts discovered by the feed's live subscription (subscribeFeed)
  // that aren't in `posts` yet. The feed screen decides whether to
  // auto-commit them (viewer at the top) or show a "N new posts" pill
  // (viewer scrolled down) — see commitPendingFeedPosts.
  pendingFeedPosts: Post[];
  // Per-id post cache, keyed by post id. Each detail screen selects its own
  // route's entry, so screens can never observe another screen's post (a
  // single shared "selected post" slot leaked stale content between detail
  // views on the navigation stack).
  postsById: Record<string, Post>;
  likedPosts: Set<string>;
  // Post IDs whose like status we've already queried — lets us skip re-checking
  // the same posts every time the feed grows via pagination or filters change.
  checkedLikePostIds: Set<string>;
  // Comment ids hidden by the delete-undo toast (deferredCommit), keyed by
  // post id, while the real delete hasn't committed yet — useCommentThread
  // owns the filtered comment list itself, but the *count* shown elsewhere
  // (InlineComments' header, PostCard's comment icon) reads `commentsCount`
  // off the Post, which only decrements once the commit actually lands 6s
  // later. Lifted here so both readers subtract the same pending set instead
  // of drifting during the undo window (panel-system.md review, note 7).
  pendingHiddenCommentIds: Record<string, Record<string, true>>;

  // Pagination — feed.
  // The feed currently has a single ordering (createdAt desc) and no client
  // filters, so `lastDoc` is unambiguous. If a sort or filter UI is ever
  // added (e.g. category=progress, sort=mostLiked), reset `lastDoc` to null
  // every time the active sort/filter key changes — otherwise pagination
  // continues from a cursor produced by a different query and you get
  // out-of-order/duplicated results. `fetchStructurePosts` already does
  // this implicitly by keying on structureId.
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
  // Pagination — "Mine" filter (getPostsByUser). fetchUserPosts resets the
  // cursor (first page); fetchMoreUserPosts appends subsequent pages.
  userPostsLastDoc: QueryDocumentSnapshot<DocumentData> | null;
  userPostsHasMore: boolean;
  isLoadingMoreUserPosts: boolean;
  // Pagination — structure posts
  structurePostsLastDoc: QueryDocumentSnapshot<DocumentData> | null;
  structurePostsHasMore: boolean;
  structurePostsStructureId: string | null;
  isLoadingMoreStructurePosts: boolean;

  // Loading states
  isLoading: boolean;
  isRefreshing: boolean;
  isFeedReady: boolean;
  isCreating: boolean;
  isDeleting: boolean;
  error: string | null;

  // Actions
  fetchFeed: (refresh?: boolean) => Promise<void>;
  // Focus-refetch variant: no-op while the last successful feed fetch is
  // younger than FEED_STALE_MS. Pull-to-refresh keeps calling fetchFeed
  // directly so a manual refresh always hits the network.
  fetchFeedIfStale: () => Promise<void>;
  fetchMorePosts: () => Promise<void>;
  fetchUserPosts: (userId: string) => Promise<void>;
  // Appends the next page of the viewer's own posts ("Mine" filter). Mirrors
  // fetchMorePosts: no-op unless there's a userId, more pages exist, and no
  // fetch is already in flight.
  fetchMoreUserPosts: (userId: string) => Promise<void>;
  fetchStructurePosts: (structureId: string, refresh?: boolean) => Promise<void>;
  fetchMoreStructurePosts: () => Promise<void>;
  fetchPost: (postId: string) => Promise<Post | null>;
  // postId is optional — forwarded to postsApi.createPost, which treats a
  // passed-in postId as a replay-safe idempotency key (see api/posts.ts).
  createPost: (data: CreatePostData, postId?: string) => Promise<Post>;
  updatePost: (postId: string, data: UpdatePostData) => Promise<void>;
  deletePost: (postId: string, authorId: string, structureId?: string) => Promise<void>;
  likePost: (postId: string, userId: string) => Promise<void>;
  unlikePost: (postId: string, userId: string) => Promise<void>;
  checkLiked: (postId: string, userId: string) => Promise<boolean>;
  checkFeedLikes: (userId: string) => Promise<void>;
  incrementCommentCount: (postId: string) => void;
  decrementCommentCount: (postId: string) => void;
  // Marks/clears one comment as pending-hidden for a post — see
  // `pendingHiddenCommentIds` above.
  markCommentPendingHidden: (postId: string, commentId: string) => void;
  clearCommentPendingHidden: (postId: string, commentId: string) => void;
  subscribeMapPosts: () => void;
  unsubscribeMapPosts: () => void;
  // Live subscription for the feed's head window (see api/posts.ts
  // subscribeFeed). Idempotent, mirrors subscribeMapPosts. New posts land in
  // `pendingFeedPosts`, not `posts` directly — the feed screen decides when
  // to fold them in.
  subscribeFeed: () => void;
  unsubscribeFeed: () => void;
  // Fold pendingFeedPosts into posts (dedup by id, re-sort by createdAt
  // desc) and clear the buffer. Called either automatically (viewer at the
  // top of the feed when new posts arrive) or from the "N new posts" pill.
  commitPendingFeedPosts: () => void;
  clearError: () => void;
  reset: () => void;
  /**
   * Fetch posts eligible to be a "before" candidate for a new observation
   * on the given structure. Builds the visibility scope internally so the
   * component never touches the api layer. Returns the merged, filtered
   * list; component keeps its own local state.
   */
  fetchBeforeCandidates: (structureId: string) => Promise<Post[]>;
  /**
   * Pure post lookup by id. Unlike fetchPost, this does NOT touch
   * postsById state — it's for prefilling local component state
   * (e.g. the before-post row on the create screen).
   */
  getPostById: (postId: string) => Promise<Post | null>;
  /**
   * Fetch observation posts for the checklist evidence strip. Builds the
   * visibility scope internally; returns the merged list. The checklist
   * screen keeps its own local `evidenceByItem` state.
   */
  /** `force` skips the FEED_STALE_MS cache, for a screen refetching on focus
   *  right after the user added or edited an observation elsewhere. */
  fetchStructureEvidencePosts: (
    structureId: string,
    options?: { force?: boolean }
  ) => Promise<Post[]>;
  /**
   * Upload an image to Storage then create the post, returning the new
   * post. Folds the storage step into the store so screens never import
   * the api layer directly. Reuses the same `isCreating` loading flag and
   * optimistic feed/structure prepend as createPost.
   */
  uploadAndCreatePost: (
    imageUri: string,
    storagePath: string,
    data: Omit<CreatePostData, 'imageUrl' | 'imagePath' | 'thumbnailPath'>,
    beforeImageUri?: string
  ) => Promise<Post>;
  /**
   * Fold a post created by the offline outbox's sync engine (src/api/outbox)
   * into the store once it lands, with the same optimistic-prepend behavior
   * createPost/uploadAndCreatePost get on a direct write. Called from
   * outboxStore's syncEngine.subscribePostSynced wiring, not directly by
   * screens.
   */
  ingestSyncedPost: (post: Post) => void;
}

// Held outside zustand state because unsubscribe fns aren't serializable and
// triggering React re-renders on subscription changes is undesirable.
let mapPostsUnsub: (() => void) | null = null;

// Same reasoning as mapPostsUnsub, for the feed's live subscription.
let feedUnsub: (() => void) | null = null;

// Timestamp of the last successful feed fetch — outside zustand because no
// component renders from it. Drives the focus-refetch staleness gate.
let lastFeedFetchAt = 0;

// Short-TTL cache for the structure-scoped merged fetches (checklist
// evidence strip, before-candidate picker). Each call is 2 queries of up to
// 100 docs; without this every checklist open / post-create open re-billed
// them. Keyed by purpose + structure + viewer (results are
// visibility-scoped). Cleared on any post mutation and on reset.
const structurePostsCache = new Map<string, { at: number; posts: Post[] }>();
const invalidateStructurePostsCache = () => structurePostsCache.clear();

// Immutable patch of one postsById entry; no-op when the post isn't cached.
const patchById = (
  map: Record<string, Post>,
  postId: string,
  patch: Partial<Post>
): Record<string, Post> => (map[postId] ? { ...map, [postId]: { ...map[postId], ...patch } } : map);

// Immutable removal of one postsById entry; no-op when absent.
const removeById = (map: Record<string, Post>, postId: string): Record<string, Post> => {
  if (!map[postId]) return map;
  const { [postId]: _removed, ...rest } = map;
  return rest;
};

// Shared by createPost, uploadAndCreatePost, and ingestSyncedPost (the
// offline outbox's sync-success hook): invalidate the structure-scoped
// cache, prepend the new post to the News feed (only when it's a news post
// — the feed is news-only, see FEED_CATEGORY; a new photo observation
// shouldn't appear there) and to the active structure's thread when it
// matches (any category — threads show everything), and drop any
// pendingFeedPosts echo of the same write racing in from the live feed
// subscription.
const applyNewPostSideEffects = (
  set: (partial: Partial<PostsState>) => void,
  get: () => PostsState,
  post: Post
): void => {
  invalidateStructurePostsCache();
  const { posts, structurePosts, structurePostsStructureId, pendingFeedPosts } = get();
  set({
    posts: post.category === FEED_CATEGORY ? [post, ...posts] : posts,
    pendingFeedPosts: pendingFeedPosts.filter((p) => p.id !== post.id),
    structurePosts:
      post.structureId && post.structureId === structurePostsStructureId
        ? [post, ...structurePosts]
        : structurePosts,
  });
};

export const usePostsStore = create<PostsState>((set, get) => ({
  posts: [],
  userPosts: [],
  structurePosts: [],
  ownStructurePosts: [],
  mapPosts: [],
  structurePostBadges: {},
  pendingFeedPosts: [],
  postsById: {},
  likedPosts: new Set(),
  checkedLikePostIds: new Set(),
  pendingHiddenCommentIds: {},
  lastDoc: null,
  hasMore: true,
  userPostsLastDoc: null,
  userPostsHasMore: true,
  isLoadingMoreUserPosts: false,
  structurePostsLastDoc: null,
  structurePostsHasMore: true,
  structurePostsStructureId: null,
  isLoadingMoreStructurePosts: false,
  isLoading: false,
  isRefreshing: false,
  isFeedReady: false,
  isCreating: false,
  isDeleting: false,
  error: null,

  fetchFeed: async (refresh = false) => {
    const loadingKey = refresh ? 'isRefreshing' : 'isLoading';
    await withLoading(set, loadingKey, async () => {
      const { posts, lastDoc } = await postsApi.getFeed(
        viewerScope(),
        undefined,
        undefined,
        FEED_CATEGORY
      );
      lastFeedFetchAt = Date.now();
      // On refresh, invalidate the per-post like-check memo — but only when
      // the visible post set actually changed. Re-checking likes costs one
      // query per post (empty queries still bill a read), so an unchanged
      // page keeps its memo.
      const prev = get().posts;
      const postSetChanged =
        posts.length !== prev.length || posts.some((p, i) => p.id !== prev[i]?.id);
      set({
        posts,
        lastDoc,
        hasMore: posts.length >= 20,
        isFeedReady: true,
        ...(refresh && postSetChanged
          ? { likedPosts: new Set<string>(), checkedLikePostIds: new Set<string>() }
          : {}),
      });
    });
  },

  fetchFeedIfStale: async () => {
    const { isFeedReady, isRefreshing, isLoading } = get();
    const fresh = isFeedReady && Date.now() - lastFeedFetchAt < FEED_STALE_MS;
    if (fresh || isRefreshing || isLoading) return;
    await get().fetchFeed(true);
  },

  fetchMorePosts: async () => {
    const { lastDoc, hasMore, isLoading, posts } = get();
    if (!hasMore || isLoading || !lastDoc) return;

    await withLoading(set, 'isLoading', async () => {
      const result = await postsApi.getFeed(viewerScope(), lastDoc, undefined, FEED_CATEGORY);
      set({
        posts: [...posts, ...result.posts],
        lastDoc: result.lastDoc,
        hasMore: result.posts.length >= 20,
      });
    });
  },

  fetchUserPosts: async (userId: string) => {
    await withLoading(set, 'isLoading', async () => {
      // First page — resets the cursor so a re-entry to "Mine" (or a
      // pull-to-refresh there) doesn't continue from a stale prior session's
      // pagination position.
      const { posts, lastDoc } = await postsApi.getPostsByUser(userId);
      set({
        userPosts: posts,
        userPostsLastDoc: lastDoc,
        userPostsHasMore: posts.length >= 20,
      });
    });
  },

  fetchMoreUserPosts: async (userId: string) => {
    const { userPostsLastDoc, userPostsHasMore, isLoadingMoreUserPosts, userPosts } = get();
    if (!userId || !userPostsHasMore || isLoadingMoreUserPosts || !userPostsLastDoc) return;

    await withLoading(set, 'isLoadingMoreUserPosts', async () => {
      const result = await postsApi.getPostsByUser(userId, userPostsLastDoc);
      // Dedupe by id, mirroring commitPendingFeedPosts's merge convention —
      // guards against the same post appearing twice if a page boundary
      // shifts between calls (e.g. a new post lands between pages).
      const seen = new Set(userPosts.map((p) => p.id));
      const appended = result.posts.filter((p) => !seen.has(p.id));
      set({
        userPosts: [...userPosts, ...appended],
        userPostsLastDoc: result.lastDoc,
        userPostsHasMore: result.posts.length >= 20,
      });
    });
  },

  fetchStructurePosts: async (structureId: string, refresh = false) => {
    const { structurePostsStructureId, structurePosts } = get();
    // If we already have this structure loaded and it's not a refresh, noop.
    if (!refresh && structurePostsStructureId === structureId && structurePosts.length > 0) {
      return;
    }
    await withLoading(set, 'isLoading', async () => {
      const scope = viewerScope();
      // Base list is visibility-filtered (rules require it); a second
      // authorId-scoped query brings in the viewer's own private/insurance
      // posts so their thread history isn't missing entries they can see.
      const [{ posts, lastDoc }, own] = await Promise.all([
        postsApi.getPostsByStructure(structureId, scope),
        scope.kind === 'authed'
          ? postsApi.getOwnPostsByStructure(structureId, scope.uid)
          : Promise.resolve([]),
      ]);
      const hasMore = posts.length >= 20;
      set({
        structurePosts: mergeOwnPosts(posts, own, hasMore),
        ownStructurePosts: own,
        structurePostsLastDoc: lastDoc,
        structurePostsHasMore: hasMore,
        structurePostsStructureId: structureId,
      });
    });
  },

  fetchMoreStructurePosts: async () => {
    const {
      structurePostsLastDoc,
      structurePostsHasMore,
      isLoadingMoreStructurePosts,
      structurePostsStructureId,
      structurePosts,
    } = get();
    if (
      !structurePostsHasMore ||
      isLoadingMoreStructurePosts ||
      !structurePostsLastDoc ||
      !structurePostsStructureId
    ) {
      return;
    }
    await withLoading(set, 'isLoadingMoreStructurePosts', async () => {
      const result = await postsApi.getPostsByStructure(
        structurePostsStructureId,
        viewerScope(),
        structurePostsLastDoc
      );
      const hasMore = result.posts.length >= 20;
      // Re-merge the cached own list: posts that were beyond the previous
      // window slot in as the pages reach them (mergeOwnPosts dedups by id).
      set({
        structurePosts: mergeOwnPosts(
          [...structurePosts, ...result.posts],
          get().ownStructurePosts,
          hasMore
        ),
        structurePostsLastDoc: result.lastDoc,
        structurePostsHasMore: hasMore,
      });
    });
  },

  fetchPost: async (postId: string) => {
    // Read-through cache: if we already have this post loaded in any list,
    // hand it back synchronously and skip the Firestore read.
    const { posts, userPosts, structurePosts, postsById } = get();
    const cached =
      postsById[postId] ||
      posts.find((p) => p.id === postId) ||
      userPosts.find((p) => p.id === postId) ||
      structurePosts.find((p) => p.id === postId);
    if (cached) {
      set({ postsById: { ...postsById, [postId]: cached } });
      return cached;
    }
    return await withLoading(set, 'isLoading', async () => {
      try {
        const post = await postsApi.getPost(postId);
        // null (not found) evicts any stale entry so the detail screen
        // shows not-found instead of an outdated copy.
        set({
          postsById: post
            ? { ...get().postsById, [postId]: post }
            : removeById(get().postsById, postId),
        });
        return post;
      } catch (error) {
        // Permission-denied is reachable (share link to a post the viewer
        // can't read under the visibility rules) — same eviction concern.
        set({ postsById: removeById(get().postsById, postId) });
        throw error;
      }
    });
  },

  createPost: async (data: CreatePostData, postId?: string) => {
    return await withLoading(set, 'isCreating', async () => {
      assertWritableAccount();
      const post = await postsApi.createPost(data, postId);
      // Optimistically prepend to the main feed and — if the new post is
      // attached to the structure currently loaded in the structure-detail
      // view — to that list too, so RecentActivitySection updates without
      // a refresh.
      applyNewPostSideEffects(set, get, post);
      return post;
    });
  },

  updatePost: async (postId: string, data: UpdatePostData) => {
    set({ error: null });

    try {
      assertWritableAccount();
      await postsApi.updatePost(postId, data);
      invalidateStructurePostsCache();

      // `null` is the API-level "delete this field" sentinel (mapped to
      // deleteField in postsApi); the cached Post shape models absence as
      // undefined, so translate before merging. The explicit-undefined keys
      // intentionally overwrite the stale cached values.
      const {
        checklistItemId,
        checklistItemLabel,
        location,
        authorAvatarUrl,
        address,
        title,
        ...rest
      } = data;
      const patch = {
        ...rest,
        ...(address !== undefined ? { address: address ?? undefined } : {}),
        ...(title !== undefined ? { title: title ?? undefined } : {}),
        ...(checklistItemId !== undefined ? { checklistItemId: checklistItemId ?? undefined } : {}),
        ...(checklistItemLabel !== undefined
          ? { checklistItemLabel: checklistItemLabel ?? undefined }
          : {}),
        ...(authorAvatarUrl !== undefined ? { authorAvatarUrl: authorAvatarUrl ?? undefined } : {}),
        // Mirror postsApi.updatePost's geohash recompute so the cached Post
        // (which requires it) stays in sync with what the server stored.
        ...(location
          ? {
              location: {
                ...location,
                geohash: ngeohash.encode(location.latitude, location.longitude, 7),
              },
            }
          : {}),
        // Mirror postsApi.updatePost's masking enforcement: an anonymous
        // patch always forces the masked identity locally too, so the cache
        // can't drift from the server's stored doc even if a caller forgot
        // to mask authorName/authorAvatarUrl itself.
        ...(data.isAnonymous === true ? { authorName: '', authorAvatarUrl: undefined } : {}),
      };

      const { posts, userPosts, mapPosts, postsById } = get();
      const nextMapPosts = updateById(mapPosts, postId, patch);
      set({
        posts: updateById(posts, postId, patch),
        userPosts: updateById(userPosts, postId, patch),
        mapPosts: nextMapPosts,
        structurePostBadges: deriveStructurePostBadges(nextMapPosts),
        postsById: patchById(postsById, postId, patch),
      });
    } catch (error) {
      set({ error: getErrorMessage(error) });
      throw error;
    }
  },

  deletePost: async (postId: string, authorId: string, structureId?: string) => {
    await withLoading(set, 'isDeleting', async () => {
      assertWritableAccount();
      await postsApi.deletePost(postId, authorId, structureId);
      invalidateStructurePostsCache();

      const { posts, userPosts, structurePosts, mapPosts, postsById } = get();
      const nextMapPosts = mapPosts.filter((p) => p.id !== postId);
      set({
        posts: posts.filter((p) => p.id !== postId),
        userPosts: userPosts.filter((p) => p.id !== postId),
        structurePosts: structurePosts.filter((p) => p.id !== postId),
        mapPosts: nextMapPosts,
        structurePostBadges: deriveStructurePostBadges(nextMapPosts),
        postsById: removeById(postsById, postId),
      });
    });
  },

  likePost: async (postId: string, userId: string) => {
    // Guard before the optimistic update: a blocked write must not flash a
    // like on and roll it back. Quiet no-op (not a throw) — like taps are
    // fire-and-forget and the UI entry points already show the upgrade sheet.
    if (anonWritesBlocked(useAuthStore.getState().user)) return;
    const { posts, postsById, likedPosts } = get();

    // Optimistic update
    const newLikedPosts = new Set(likedPosts);
    newLikedPosts.add(postId);

    const likeCount = (posts.find((p) => p.id === postId) ?? postsById[postId])?.likesCount ?? 0;
    set({
      posts: updateById(posts, postId, { likesCount: likeCount + 1 } as Partial<Post>),
      postsById: patchById(postsById, postId, { likesCount: likeCount + 1 }),
      likedPosts: newLikedPosts,
    });

    try {
      await postsApi.likePost(postId, userId);
    } catch (error) {
      // Rollback on error
      newLikedPosts.delete(postId);
      set({
        posts: updateById(posts, postId, { likesCount: likeCount } as Partial<Post>),
        postsById: patchById(get().postsById, postId, { likesCount: likeCount }),
        likedPosts: newLikedPosts,
        error: getErrorMessage(error),
      });
    }
  },

  unlikePost: async (postId: string, userId: string) => {
    // Same guard-before-optimistic-update as likePost above.
    if (anonWritesBlocked(useAuthStore.getState().user)) return;
    const { posts, postsById, likedPosts } = get();

    // Optimistic update
    const newLikedPosts = new Set(likedPosts);
    newLikedPosts.delete(postId);

    const likeCount = (posts.find((p) => p.id === postId) ?? postsById[postId])?.likesCount ?? 0;
    set({
      posts: updateById(posts, postId, { likesCount: Math.max(0, likeCount - 1) } as Partial<Post>),
      postsById: patchById(postsById, postId, { likesCount: Math.max(0, likeCount - 1) }),
      likedPosts: newLikedPosts,
    });

    try {
      await postsApi.unlikePost(postId, userId);
    } catch (error) {
      // Rollback on error
      newLikedPosts.add(postId);
      set({
        posts: updateById(posts, postId, { likesCount: likeCount } as Partial<Post>),
        postsById: patchById(get().postsById, postId, { likesCount: likeCount }),
        likedPosts: newLikedPosts,
        error: getErrorMessage(error),
      });
    }
  },

  checkLiked: async (postId: string, userId: string) => {
    try {
      const liked = await postsApi.hasLiked(postId, userId);
      if (liked) {
        const { likedPosts } = get();
        const newLikedPosts = new Set(likedPosts);
        newLikedPosts.add(postId);
        set({ likedPosts: newLikedPosts });
      }
      return liked;
    } catch {
      return false;
    }
  },

  checkFeedLikes: async (userId: string) => {
    const { posts, checkedLikePostIds, likedPosts } = get();
    if (posts.length === 0) return;
    // Only query posts we haven't already checked. Prior implementation
    // re-checked every post on every pagination load.
    const toCheck = posts.map((p) => p.id).filter((id) => !checkedLikePostIds.has(id));
    if (toCheck.length === 0) return;
    try {
      const newlyLiked = await postsApi.batchCheckLikes(toCheck, userId);
      const mergedLiked = new Set(likedPosts);
      newlyLiked.forEach((id) => mergedLiked.add(id));
      const mergedChecked = new Set(checkedLikePostIds);
      toCheck.forEach((id) => mergedChecked.add(id));
      set({ likedPosts: mergedLiked, checkedLikePostIds: mergedChecked });
    } catch {
      // Silently handle — likes will just show as unchecked
    }
  },

  incrementCommentCount: (postId: string) => {
    const { posts, postsById } = get();
    set({
      posts: updateById(posts, postId, {
        commentsCount: (posts.find((p) => p.id === postId)?.commentsCount ?? 0) + 1,
      } as Partial<Post>),
      postsById: patchById(postsById, postId, {
        commentsCount: (postsById[postId]?.commentsCount ?? 0) + 1,
      }),
    });
  },

  decrementCommentCount: (postId: string) => {
    const { posts, postsById } = get();
    set({
      posts: updateById(posts, postId, {
        commentsCount: Math.max(0, (posts.find((p) => p.id === postId)?.commentsCount ?? 0) - 1),
      } as Partial<Post>),
      postsById: patchById(postsById, postId, {
        commentsCount: Math.max(0, (postsById[postId]?.commentsCount ?? 0) - 1),
      }),
    });
  },

  markCommentPendingHidden: (postId: string, commentId: string) => {
    const { pendingHiddenCommentIds } = get();
    set({
      pendingHiddenCommentIds: {
        ...pendingHiddenCommentIds,
        [postId]: { ...pendingHiddenCommentIds[postId], [commentId]: true },
      },
    });
  },

  clearCommentPendingHidden: (postId: string, commentId: string) => {
    const { pendingHiddenCommentIds } = get();
    const forPost = pendingHiddenCommentIds[postId];
    if (!forPost || !(commentId in forPost)) return;
    const nextForPost = { ...forPost };
    delete nextForPost[commentId];
    set({
      pendingHiddenCommentIds: { ...pendingHiddenCommentIds, [postId]: nextForPost },
    });
  },

  subscribeMapPosts: () => {
    // Idempotent: bail if already subscribed. MapScreen mounts once per app
    // session in Expo Router, but guard against StrictMode double-invocation
    // and hot reloads.
    if (mapPostsUnsub) return;
    mapPostsUnsub = postsApi.subscribeRecentPosts(
      (mapPosts) => {
        set({ mapPosts, structurePostBadges: deriveStructurePostBadges(mapPosts) });
      },
      (error) => {
        // Map layer is ambient; log instead of surfacing an error banner.
        log.error('map posts subscription failed:', error);
      }
    );
  },

  unsubscribeMapPosts: () => {
    if (mapPostsUnsub) {
      mapPostsUnsub();
      mapPostsUnsub = null;
    }
    set({ mapPosts: [], structurePostBadges: {} });
  },

  subscribeFeed: () => {
    // Idempotent: the feed screen's useFocusEffect subscribes on every
    // focus, so guard against re-subscribing on rapid focus/blur churn.
    if (feedUnsub) return;
    feedUnsub = postsApi.subscribeFeed(
      viewerScope(),
      (snapshotPosts) => {
        const { isFeedReady, posts, pendingFeedPosts } = get();
        // Bail until the feed's first fetchFeed has resolved — before that
        // there's no posts[0] cursor to compare newness against, and
        // fetchFeed's own set() would otherwise race an early snapshot.
        if (!isFeedReady) return;
        const newestKnownAt = posts[0]?.createdAt.toMillis() ?? 0;
        const knownIds = new Set([...posts.map((p) => p.id), ...pendingFeedPosts.map((p) => p.id)]);
        // Only genuinely-new posts (unknown id, strictly newer than the
        // current top of feed) qualify — likes/comment count changes on
        // existing posts are deliberately NOT reconciled here, so an
        // optimistic like doesn't get fought by the next snapshot fire.
        const fresh = snapshotPosts.filter(
          (p) => !knownIds.has(p.id) && p.createdAt.toMillis() > newestKnownAt
        );
        if (fresh.length === 0) return;
        const merged = [...fresh, ...pendingFeedPosts].sort(
          (a, b) => b.createdAt.toMillis() - a.createdAt.toMillis()
        );
        set({ pendingFeedPosts: merged });
      },
      (error) => {
        // Live feed head is a nice-to-have on top of fetchFeedIfStale/pull-
        // to-refresh; log instead of surfacing an error banner.
        log.error('feed subscription failed:', error);
      },
      undefined,
      FEED_CATEGORY
    );
  },

  unsubscribeFeed: () => {
    if (feedUnsub) {
      feedUnsub();
      feedUnsub = null;
    }
    set({ pendingFeedPosts: [] });
  },

  commitPendingFeedPosts: () => {
    const { posts, pendingFeedPosts } = get();
    if (pendingFeedPosts.length === 0) return;
    const seen = new Set<string>();
    const merged: Post[] = [];
    for (const p of [...pendingFeedPosts, ...posts]) {
      if (seen.has(p.id)) continue;
      seen.add(p.id);
      merged.push(p);
    }
    merged.sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis());
    set({ posts: merged, pendingFeedPosts: [] });
  },

  getPostById: async (postId) => {
    return await postsApi.getPost(postId);
  },

  fetchStructureEvidencePosts: async (structureId, options) => {
    // Visibility-scoped base query plus the viewer's own posts: checklist
    // evidence is mostly the claim owner's own photos, which may be private
    // or insurance-only and would otherwise drop out of the list.
    const scope = viewerScope();
    const cacheKey = `evidence:${structureId}:${scope.kind === 'authed' ? scope.uid : 'anon'}`;
    const cached = structurePostsCache.get(cacheKey);
    if (!options?.force && cached && Date.now() - cached.at < FEED_STALE_MS) return cached.posts;

    const [{ posts }, own] = await Promise.all([
      postsApi.getPostsByStructure(structureId, scope, undefined, 100),
      scope.kind === 'authed'
        ? postsApi.getOwnPostsByStructure(structureId, scope.uid, 100)
        : Promise.resolve([]),
    ]);
    const merged = mergeOwnPosts(posts, own, false);
    structurePostsCache.set(cacheKey, { at: Date.now(), posts: merged });
    return merged;
  },

  uploadAndCreatePost: async (imageUri, storagePath, data, beforeImageUri) => {
    return await withLoading(set, 'isCreating', async () => {
      // Guard before the uploads, not just the doc write — storage.rules
      // would reject the upload anyway, but failing here is cheaper and
      // gives friendly copy.
      assertWritableAccount();
      // Upload primary (and optional before) image in parallel.
      const beforeStoragePath = beforeImageUri
        ? storagePath.replace('.jpg', '-before.jpg')
        : undefined;
      const [{ downloadUrl, storagePath: uploadedPath }, beforeResult] = await Promise.all([
        uploadImage(imageUri, storagePath),
        beforeStoragePath ? uploadImage(beforeImageUri!, beforeStoragePath) : Promise.resolve(null),
      ]);
      // Predict the Resize Images extension thumbnail/display paths so
      // onImageResized can fill in thumbnailUrl/displayUrl after upload.
      const thumbnailPath = thumbnailPathFor(uploadedPath) ?? undefined;
      const displayPath = displayPathFor(uploadedPath) ?? undefined;
      const beforeFields =
        beforeResult != null
          ? {
              beforeImageUrl: beforeResult.downloadUrl,
              beforeImagePath: beforeResult.storagePath,
              beforeThumbnailPath: thumbnailPathFor(beforeResult.storagePath) ?? undefined,
            }
          : {};
      // The postId embedded in storagePath (posts/{uid}/{postId}.jpg) is
      // threaded through as the Firestore doc id too, so a retried upload
      // (e.g. from the offline outbox) can't create a duplicate post.
      const postId = uploadedPath.match(/\/([^/]+)\.jpg$/)?.[1];
      const post = await postsApi.createPost(
        {
          ...data,
          imageUrl: downloadUrl,
          imagePath: uploadedPath,
          thumbnailPath,
          displayPath,
          ...beforeFields,
        },
        postId
      );
      // Optimistically prepend to feed and the active structure's list; same
      // side effects as createPost above.
      applyNewPostSideEffects(set, get, post);
      return post;
    });
  },

  ingestSyncedPost: (post) => {
    // Defensive dedup: an item the outbox already reported as synced
    // (or one this tab created directly and is now double-reported by a
    // stale engine notification) shouldn't double-prepend into the feed.
    if (get().posts.some((p) => p.id === post.id)) return;
    applyNewPostSideEffects(set, get, post);
  },

  fetchBeforeCandidates: async (structureId) => {
    // The structure query is visibility-scoped (rules require it); the
    // poster's own private/insurance posts come from the authorId query so
    // they can still pair against their earlier non-public photos.
    const scope = viewerScope();
    const cacheKey = `before:${structureId}:${scope.kind === 'authed' ? scope.uid : 'anon'}`;
    const cached = structurePostsCache.get(cacheKey);
    if (cached && Date.now() - cached.at < FEED_STALE_MS) return cached.posts;

    const [{ posts }, own] = await Promise.all([
      postsApi.getPostsByStructure(structureId, scope, undefined, 50),
      scope.kind === 'authed'
        ? postsApi.getOwnPostsByStructure(structureId, scope.uid, 50)
        : Promise.resolve([]),
    ]);
    // A post that is itself an "after" can't also serve as a "before" —
    // keeps pairs one level deep instead of forming chains.
    const candidates = mergeOwnPosts(posts, own, false).filter(
      (p) => !p.beforePostId && !p.beforeImageUrl
    );
    structurePostsCache.set(cacheKey, { at: Date.now(), posts: candidates });
    return candidates;
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    if (mapPostsUnsub) {
      mapPostsUnsub();
      mapPostsUnsub = null;
    }
    if (feedUnsub) {
      feedUnsub();
      feedUnsub = null;
    }
    lastFeedFetchAt = 0;
    invalidateStructurePostsCache();
    set({
      posts: [],
      userPosts: [],
      structurePosts: [],
      ownStructurePosts: [],
      mapPosts: [],
      structurePostBadges: {},
      pendingFeedPosts: [],
      postsById: {},
      likedPosts: new Set(),
      checkedLikePostIds: new Set(),
      lastDoc: null,
      hasMore: true,
      userPostsLastDoc: null,
      userPostsHasMore: true,
      isLoadingMoreUserPosts: false,
      structurePostsLastDoc: null,
      structurePostsHasMore: true,
      structurePostsStructureId: null,
      isLoadingMoreStructurePosts: false,
      isLoading: false,
      isRefreshing: false,
      isFeedReady: false,
      isCreating: false,
      isDeleting: false,
      error: null,
    });
  },
}));
